<template>
    <NuxtLink to="#main" class="sr-only">Skip to main content</NuxtLink>
    <NuxtLayout>
        <NuxtPage id="main" role="main"/>
    </NuxtLayout>
</template>

<script setup lang="ts">
declare const window: any
import {useCartModalStore, useScriptEventPage} from '#imports'
import {joinURL, withQuery} from 'ufo'
import {setDynamicYieldRecommendationContext} from '~/vendors/dynamicYield'
import {gtmOptimize, gtmStart} from '~/vendors/google-tag-manager'
import {useGtmStore} from '~/stores/googleTagManagerStore'
import {useLocaleStore} from '~/stores/localeStore'
import useSiteService from '~/composables/siteService'
import { isClient} from '@vueuse/core';
import { URL_CONFIG } from "~~/shared/utils/url.const"
const { getCustomer, addToCart } = useSiteService()

const nuxtApp = useNuxtApp()
const userStore = useUserStore()
const gtmStore = useGtmStore()
const localeStore = useLocaleStore()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const config = nuxtApp.$config
const dyReadyToLoad = ref(false)
const gtmReadyToLoad = ref(false)
const cartModalStore = useCartModalStore()

useSchemaOrg([
  defineWebPage({
    // name: "Clinically-backed support for calm, clarity & sleep | Thorne",
    url: "https://www.thorne.com",
    isPartOf: {
      "@type": "WebSite",
      id: "https://www.thorne.com/#website"
    },
    about: {
      "@type": "Organization",
      id: "https://www.thorne.com/#organization"
    },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: "https://d1vo8zfysxy97v.cloudfront.net/images/layout/thorne-logo.svg"
    }

  }),
])

if (import.meta.client) {
  window.polarisOptions = {
    GoogleAnalyticsTrackingId: config.public.googleTagManagerKey,
    hideCookieButton: true
  }
}

const polaris = useScript(
  {
    src: config.public.trueVaultPolarisURL,
    fetchpriority: 'high',
    defer: false,
    crossorigin: false,
    referrerpolicy: 'strict-origin-when-cross-origin',
  }
)

const dyScriptId = computed(() => {
  return localeStore.locale?.localeCode == 'en-CA' ? config.public.dynamicYieldSectionIdCA : nuxtApp.$config.public.dynamicYieldSectionIdUs;
})
const dynamicYield_dynamic = useScript(
    {
      src: `//cdn.dynamicyield.com/api/${dyScriptId.value}/api_dynamic.js`,
      fetchpriority: 'high',
      defer: false,
      crossorigin: false,
      referrerpolicy: 'strict-origin-when-cross-origin',
    },
    {
      trigger: dyReadyToLoad
    }
)
const dynamicYield_static = useScript(
    {
      src: `//cdn.dynamicyield.com/api/${dyScriptId.value}/api_static.js`,
      crossorigin: false,
      referrerpolicy: 'strict-origin-when-cross-origin',
    },
    {
      trigger: dyReadyToLoad
    }
)
const gtm = useScript(
    {
      src: `//www.googletagmanager.com/gtm.js?id=${config.public.googleTagManagerKey}`,
      fetchpriority: 'high',
      defer: false,
      crossorigin: false,
      referrerpolicy: 'strict-origin-when-cross-origin',
    },
    {
      trigger: gtmReadyToLoad
    }
)

polaris.onLoaded(()=>{
  gtmReadyToLoad.value = true
})
gtm.onLoaded(() => {
  const gtmKey = config.public.googleTagManagerKey
  const gtmOptimizeKey = config.public.googleOptimizeKey ?? ''

  let keyParam = {}
  if (gtmOptimizeKey) {
    keyParam[gtmOptimizeKey] = true
  }

  gtmStore.pushVisitorInformation();
  gtmStore.pushAttributes();

  gtmOptimize(window, document.documentElement, 'async-hide', 'dataLayer', 4000, keyParam);
  gtmStart(window, document, 'script', 'dataLayer', gtmKey);
})

// initialize Dynamic Yield and fetch choices
const dyStore = useDynamicYieldStore();
await useAsyncData(`dy-data-${route.path}`, () => dyStore.fetchChoices(route.path));

// lazily fetch cart data
const cartStore = useCartStore();
useAsyncData('cart-data', () => cartStore.fetch(), {
  server: false,
  lazy: true
});

onMounted(async () => {

  document.addEventListener('ADD_TO_CART_EVENT', async (eventData) => {
    try {
      const addToCartResponse: any = await addToCart(eventData.detail.id, eventData.detail.quantity);
      const modalData = addToCartResponse.lines.find(line => line.productId === eventData.detail.id);
      const totalQuantity = addToCartResponse.lines.reduce((sum, line) => sum + line.quantity, 0)
      cartModalStore.open({
            name: modalData.name,
            subtotal: modalData.subtotal,
            imageUrl: modalData.imageUrl,
            quantity: modalData.quantity,
            id: `add-to-cart-${eventData.detail.id}`
          }, totalQuantity
      )
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  });

  if (isDynamicYieldEnabled(config.public.dynamicYieldEnabled)) {
    window.DY = window.DY || {}
    setDynamicYieldRecommendationContext(route.fullPath, window.DY)
    dyStore.pushVariantsToGtm();

    dyReadyToLoad.value = true
  }

  document.addEventListener('focusin', (e) => {
    // prevent focus from being set on the polaris lockdown container
    // this is a workaround for a bug/issue where polaris steals focus from the main content when the script is loaded
    const currentFocused: EventTarget | null = e.target;
    const lastFocused: EventTarget | null = e.relatedTarget;

    if (!!currentFocused && currentFocused !== lastFocused) {
      if (currentFocused instanceof HTMLElement && lastFocused instanceof HTMLElement) {
        const currentFocusedEle: HTMLElement = currentFocused as HTMLElement;
        const lastFocusedEle: HTMLElement = lastFocused as HTMLElement;

        if (currentFocusedEle.id === 'polaris-css-lockdown-container') {
          currentFocusedEle.blur(); // remove focus from polaris lockdown container
          lastFocusedEle.focus(); // add focus back to the last focused element
        }
      }
    }
  });

  if (isClient) {
    const script = document.createElement('script');
    script.src = window.location.origin + '/taia/chatbot.js';
    script.async = true;
    script.addEventListener('load', () => {
      window.dispatchEvent(new CustomEvent('taia-chatbot-ready'));
    });
    document.body.appendChild(script);
  }
 
  if (config.public.yotpoStoreId){
    const script = document.createElement('script')
    script.src = `${URL_CONFIG.YOTPO_URL}/${config.public.yotpoStoreId}/`
    script.async = true
    document.head.appendChild(script)
  }

  const data = await getCustomer();
  data && localeStore.setCustomer(data);
});

useScriptEventPage(({title, path}) => {
  gtmStore.pageViewEvent(path, title);
})

function isDynamicYieldEnabled(flag: string | boolean): boolean {
  if (typeof flag === "boolean") {
    return flag
  } else {
    return flag.toLowerCase() === 'true'
  }
}

// fetch locales - top level await, only fetches once on the server
await localeStore.fetchLocales();

// parse locale query param, needs to be done after locales are loaded into the store/state
const localeQuery = route.query.locale;
const localeQueryValue = Array.isArray(localeQuery) ? localeQuery[0]?.toString() : localeQuery?.toString();
if (!!localeQueryValue) {
  localeStore.setLocale(localeQueryValue);
}

const getLocalizedUrl = (locale) => {
  let url = runtimeConfig.public.thorneSite;
  let path = route.path;
  if (!!locale) {
    if (!!locale.domain) {
      url = locale.domain;
    }
    else if (!!locale.localeCode) {
      path = withQuery(path, { locale: locale.localeCode });
    }
  }
  return joinURL(url, path);
}

// setup locale (canonical and href) links
const localeLinks = computed<any[]>(() => localeStore.locales.map(val => {
  return {
    href: getLocalizedUrl(val),
    hreflang: val.localeCode,
    rel: 'alternate'
  }
}));

localeLinks.value.push({
  href: joinURL(runtimeConfig.public.thorneSite, route.path),
  hreflang: 'x-default',
  rel: 'alternate'
});

localeLinks.value.push({
  href: getLocalizedUrl(localeStore.locale), 
  rel: 'canonical' 
});



const taiaChatCss = computed(() => [{
  href: '/taia/chatbot.css',
  rel: 'stylesheet'
}]);

useHead({
  htmlAttrs: {
      lang: localeStore.locale?.language
  },
  link: [
    ...localeLinks.value,
    ...taiaChatCss.value
  ],
  script: [
    {
      src: 'https://static.truemed.com/widgets/merchant-pdp-widget.min.js',
      defer: true
    },
    {
    src: config.public.orderGrooveScriptUrl,
    defer: true
    }
  ]
});
</script>
